import type { FieldErrors, UseFormRegister } from "react-hook-form";
import { ChevronDown, Timer } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  FieldError,
  Label,
  fieldControlClass,
  fieldErrorId,
} from "@/components/ui";
import { SectionCard } from "@/components/common";
import type { ActivityDetailFormValues } from "../activityDetailForm";

interface BookingCutoffSectionProps {
  formId: string;
  register: UseFormRegister<ActivityDetailFormValues>;
  errors: FieldErrors<ActivityDetailFormValues>;
  modified: boolean;
}

export function BookingCutoffSection({
  formId,
  register,
  errors,
  modified,
}: BookingCutoffSectionProps) {
  const { t } = useTranslation("activities");

  const valueId = `${formId}-booking-cutoff-value`;
  const unitId = `${formId}-booking-cutoff-unit`;
  const groupId = `${formId}-booking-cutoff`;
  const cutoffErrorId = fieldErrorId(groupId);
  const cutoffError = errors.booking_cutoff_value?.message;
  const hasCutoffError = !!(
    errors.booking_cutoff_value || errors.booking_cutoff_unit
  );

  return (
    <SectionCard
      title={t("detailForm.bookingCutoffTitle")}
      icon={Timer}
      modified={modified}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
        <div className="max-w-2xl min-w-0 space-y-1 text-[13px] leading-relaxed">
          <p className="text-surface-600">
            {t("detailForm.bookingCutoffHint")}
          </p>
          <p className="text-surface-500">
            {t("detailForm.bookingCutoffExample")}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-3">
          <div className="flex w-28 flex-col gap-1.5">
            <Label
              htmlFor={valueId}
              spacing="none"
            >
              {t("detailForm.bookingCutoffLeadTime")}
            </Label>
            <input
              id={valueId}
              form={formId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              aria-invalid={hasCutoffError || undefined}
              aria-describedby={hasCutoffError ? cutoffErrorId : undefined}
              className={fieldControlClass({ className: "tabular-nums" })}
              {...register("booking_cutoff_value")}
            />
          </div>
          <div className="flex w-40 flex-col gap-1.5">
            <Label
              htmlFor={unitId}
              spacing="none"
            >
              {t("detailForm.bookingCutoffUnitLabel")}
            </Label>
            <div className="relative">
              <select
                id={unitId}
                form={formId}
                aria-invalid={hasCutoffError || undefined}
                aria-describedby={hasCutoffError ? cutoffErrorId : undefined}
                className={fieldControlClass({
                  className: "cursor-pointer appearance-none pr-9",
                })}
                {...register("booking_cutoff_unit")}
              >
                <option value="minutes">
                  {t("detailForm.bookingCutoffUnitMinutes")}
                </option>
                <option value="hours">
                  {t("detailForm.bookingCutoffUnitHours")}
                </option>
              </select>
              <ChevronDown
                className="text-surface-400 pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2"
                aria-hidden
              />
            </div>
          </div>
        </div>
      </div>
      {cutoffError ? (
        <FieldError
          id={cutoffErrorId}
          className="mt-2"
        >
          {String(cutoffError)}
        </FieldError>
      ) : null}
    </SectionCard>
  );
}
